import { useEffect, useLayoutEffect, useState } from "react";

// The look of the steel pieces (buttons, bars, plates). Steel is the standard. A member with a login can pick Gold, and a
// Premium member can also pick Quartz (polished white Calacatta quartz with gold veins and trim). Everyone sees all three in
// the list; a guest or free member who presses one that is not theirs gets a 10 second sample, then a sign-up or Premium note. The choice is kept on
// the device (like the caption settings), and put on the page as <html data-finish="gold"> or "quartz"; steel has no
// attribute. The paint for each finish is components/finishes.css. Guests always see steel.
export const FINISHES = [["steel", "Steel"], ["gold", "Gold"], ["quartz", "Quartz"]];
const KEY = "dtm_finish";
const CHANGED = "dtm-finish-changed";

export function readFinish() {
  try {
    const v = localStorage.getItem(KEY);
    return v === "gold" || v === "quartz" ? v : "steel";
  } catch { return "steel"; }
}

export function saveFinish(f) {
  try {
    if (f === "gold" || f === "quartz") localStorage.setItem(KEY, f);
    else localStorage.removeItem(KEY);
  } catch { /* storage blocked: the choice lasts until the page is reloaded */ }
  window.dispatchEvent(new Event(CHANGED));
}

// The photo of the quartz slab (components/quartzSlab.js) is only fetched when the Quartz look is used. It is handed to the
// page as three CSS variables on <html> (--slab-strip, --slab-strip-flip, --slab-box, --slab-square) that components/finishes.css paints with.
// If it cannot be fetched, Quartz is simply plain white.
let slabLoad = null;
export function loadSlab() {
  if (!slabLoad) slabLoad = import("../components/quartzSlab").then((m) => m.default).catch(() => { slabLoad = null; return null; });
  return slabLoad;
}

// What this person is allowed to see: guests get steel; Quartz is for Premium, so a free member who had it falls back to Gold.
export function allowedFinish(f, loggedIn, premium) {
  if (!loggedIn) return "steel";
  if (f === "quartz" && !premium) return "gold";
  return f === "gold" || f === "quartz" ? f : "steel";
}

// Put the finish on the page. Used once, by the player (it is on every page). `sample` is a look a person is only trying for
// a few seconds (Gold for a guest, Quartz for a guest or a free member); it shows on the page but is never saved, so when it ends the page goes back to
// the look they had. Returns { showing: what is on the page now, own: the look they have chosen and are allowed }.
export function useApplyFinish(loggedIn, premium, sample) {
  const [chosen, setChosen] = useState(readFinish);
  useEffect(() => {
    const on = () => setChosen(readFinish());
    window.addEventListener(CHANGED, on);
    window.addEventListener("storage", on);
    return () => { window.removeEventListener(CHANGED, on); window.removeEventListener("storage", on); };
  }, []);
  const own = allowedFinish(chosen, loggedIn, premium);
  const showing = sample || own;
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (showing === "steel") root.removeAttribute("data-finish");
    else root.setAttribute("data-finish", showing);
  }, [showing]);
  useEffect(() => () => document.documentElement.removeAttribute("data-finish"), []);
  useEffect(() => {
    if (showing !== "quartz") return undefined;
    let live = true;
    loadSlab().then((slab) => {
      if (!live || !slab) return;
      const st = document.documentElement.style;
      st.setProperty("--slab-strip", slab.strip);
      st.setProperty("--slab-strip-flip", slab.stripFlip);
      st.setProperty("--slab-box", slab.box);
      st.setProperty("--slab-square", slab.square);
    });
    return () => { live = false; };
  }, [showing]);
  return { showing, own };
}
